import { zodResolver } from '@hookform/resolvers/zod'
import { FileText, Info, Loader2, Plus } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal, promptModal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../../shared/format'
import { useSaveParty, useSaveTransaction } from '../api'
import { REVIEW_STATUSES, TX_KINDS, VAT_RATES, WITHHOLDING_RATES, type Transaction, type TxKind } from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { CheckRow, Field, FormSection } from './FormBits'
import { useFinanceDepartments } from '../departments'

type Props = { open: boolean; transaction: Transaction | null; defaultKind?: TxKind; onOpenChange: (v: boolean) => void }

export function TransactionFormModal({ open, onOpenChange, ...rest }: Props) {
  return open ? <TxForm key={rest.transaction?.id ?? 'new'} {...rest} onDone={() => onOpenChange(false)} /> : null
}

function TxForm({ transaction: tx, defaultKind = 'expense', onDone }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveTransaction()
  const createParty = useSaveParty()
  const { categories, parties } = useFinanceLookups()
  const departments = useFinanceDepartments()
  const today = new Date().toISOString().slice(0, 10)

  const schema = z
    .object({
      kind: z.enum(TX_KINDS),
      counterpartyId: z.string().min(1, t('finance.tx.form.partyRequired')),
      categoryId: z.string().min(1, t('finance.tx.form.categoryRequired')),
      docNo: z.string().trim().max(40),
      date: z.string().min(1, t('finance.form.dateRequired')),
      dueDate: z.string().min(1, t('finance.form.dateRequired')),
      description: z.string().trim().min(2, t('finance.tx.form.descriptionRequired')).max(160),
      net: z.string().regex(/^\d+(\.\d{1,2})?$/, t('finance.form.amountInvalid')).refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
      vatRate: z.string(),
      withholdingRate: z.string(),
      hasDocument: z.boolean(),
      review: z.enum(REVIEW_STATUSES),
      accountantNote: z.string().trim().max(240),
      departmentId: z.string(),
    })
    .refine((v) => !v.date || !v.dueDate || v.dueDate >= v.date, { path: ['dueDate'], message: t('finance.form.dueBeforeDate') })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      kind: tx?.kind ?? defaultKind,
      counterpartyId: tx?.counterpartyId ?? '',
      categoryId: tx?.categoryId ?? '',
      docNo: tx?.docNo ?? '',
      date: tx?.date ?? today,
      dueDate: tx?.dueDate ?? today,
      description: tx?.description ?? '',
      net: tx?.net ?? '',
      vatRate: String(tx?.vatRate ?? 20),
      withholdingRate: String(tx?.withholdingRate ?? 0),
      hasDocument: tx?.hasDocument ?? true,
      review: tx?.review ?? 'pending',
      accountantNote: tx?.accountantNote ?? '',
      departmentId: tx?.departmentId ?? '',
    },
  })
  const { errors } = form.formState
  const [kind, date] = useWatch({ control: form.control, name: ['kind', 'date'] })
  const partyKind = kind === 'income' ? 'customer' : 'supplier'

  const addParty = async () => {
    const name = await promptModal({ title: t(kind === 'income' ? 'finance.tx.form.newCustomer' : 'finance.tx.form.newSupplier'), placeholder: t('finance.tx.form.partyName'), confirmLabel: t('common.add') })
    if (!name?.trim()) return
    try {
      const p = await createParty.mutateAsync({ input: { name, kind: partyKind, taxNo: '', taxOffice: '', bankName: '', iban: '', email: '', phone: '' } })
      form.setValue('counterpartyId', p.id, { shouldValidate: true })
    } catch (e) {
      toastError(e)
    }
  }

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const saved = await save.mutateAsync({
        id: tx?.id,
        input: { ...v, vatRate: Number(v.vatRate), withholdingRate: v.kind === 'expense' ? Number(v.withholdingRate) : 0 },
      })
      toast.success(t(tx ? 'finance.tx.updated' : 'finance.tx.created'), { description: t('finance.tx.totalInfo', { total: fmtMoney(saved.total) }) })
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<FileText />}
      title={t(tx ? 'finance.tx.form.editTitle' : 'finance.tx.form.createTitle')}
      description={t('finance.tx.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <Controller
          control={form.control}
          name="kind"
          render={({ field }) => (
            <div className="grid grid-cols-2 rounded-lg border bg-muted/40 p-1" role="radiogroup" aria-label={t('finance.tx.form.kind')}>
              {TX_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={field.value === k}
                  disabled={!!tx}
                  onClick={() => {
                    field.onChange(k)
                    form.setValue('categoryId', '')
                    form.setValue('counterpartyId', '')
                  }}
                  className={cn('rounded-md py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed', field.value === k ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                >
                  {t(`finance.kind.${k}`)}
                </button>
              ))}
            </div>
          )}
        />

        <FormSection title={t('finance.tx.form.sectionParty')}>
          <Field id="tx-party" label={t(kind === 'income' ? 'finance.customer' : 'finance.supplier')} error={errors.counterpartyId?.message}>
            <div className="flex gap-2">
              <FormSelect
                id="tx-party"
                className="min-w-0 flex-1"
                control={form.control}
                name="counterpartyId"
                placeholder={t('common.select')}
                options={parties.filter((p) => p.kind === partyKind).map((p) => ({ value: p.id, label: p.name, description: p.taxNo ? t('finance.taxNo', { no: p.taxNo }) : undefined }))}
              />
              <Button type="button" variant="outline" size="icon" onClick={() => void addParty()} disabled={createParty.isPending} aria-label={t(kind === 'income' ? 'finance.tx.form.newCustomer' : 'finance.tx.form.newSupplier')}>
                <Plus />
              </Button>
            </div>
          </Field>
          <Field id="tx-cat" label={t('finance.category')} error={errors.categoryId?.message}>
            <FormSelect id="tx-cat" control={form.control} name="categoryId" placeholder={t('common.select')} options={categories.filter((c) => c.kind === kind && (c.active || c.id === tx?.categoryId)).map((c) => ({ value: c.id, label: c.name }))} />
          </Field>
          {departments.data?.length ? (
            <Field id="tx-dept" label={t('finance.departments.field')} hint={t('finance.departments.fieldHint')}>
              <FormSelect
                id="tx-dept"
                control={form.control}
                name="departmentId"
                options={[{ value: '', label: t('finance.departments.categoryDefault') }, ...departments.data.map((d) => ({ value: d.id, label: d.name }))]}
              />
            </Field>
          ) : null}
          <Field id="tx-doc" label={t('finance.tx.docNo')}>
            <Input id="tx-doc" placeholder={t('finance.tx.form.docNoPlaceholder')} {...form.register('docNo')} />
          </Field>
          <Field id="tx-date" label={t('finance.tx.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="tx-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <Field id="tx-desc" label={t('finance.tx.description')} error={errors.description?.message} full>
            <Input id="tx-desc" maxLength={160} aria-invalid={!!errors.description} {...form.register('description')} />
          </Field>
        </FormSection>

        <FormSection title={t('finance.tx.form.sectionAmount')}>
          <Field id="tx-net" label={t('finance.tx.net')} error={errors.net?.message}>
            <Controller control={form.control} name="net" render={({ field }) => <NumberInput id="tx-net" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.net} />} />
          </Field>
          <Field id="tx-due" label={t('finance.tx.dueDate')} error={errors.dueDate?.message}>
            <Controller control={form.control} name="dueDate" render={({ field }) => <DateField id="tx-due" value={field.value} onChange={field.onChange} min={date || undefined} clearable={false} />} />
          </Field>
          <Field id="tx-vat" label={t('finance.tx.vatRate')}>
            <FormSelect id="tx-vat" control={form.control} name="vatRate" options={VAT_RATES.map((r) => ({ value: String(r), label: `%${r}` }))} />
          </Field>
          <Field id="tx-wh" label={t('finance.tx.withholdingRate')} hint={kind === 'income' ? t('finance.tx.form.withholdingIncome') : undefined}>
            <FormSelect
              id="tx-wh"
              control={form.control}
              name="withholdingRate"
              disabled={kind === 'income'}
              options={WITHHOLDING_RATES.map((r) => ({ value: String(r), label: r ? `%${r}` : t('common.none') }))}
            />
          </Field>
          {tx ? (
            <dl className="grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-3 text-sm sm:col-span-2 sm:grid-cols-4">
              {(['vat', 'withholding', 'total', 'remaining'] as const).map((k) => (
                <div key={k}>
                  <dt className="text-xs text-muted-foreground">{t(`finance.tx.${k}`)}</dt>
                  <dd className="font-semibold tabular-nums">{fmtMoney(tx[k])}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <p className="flex items-start gap-2 text-xs text-muted-foreground sm:col-span-2">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {t('finance.tx.form.serverCalc')}
          </p>
        </FormSection>

        <FormSection title={t('finance.tx.form.sectionAccounting')}>
          <Field id="tx-review" label={t('finance.tx.review')}>
            <FormSelect id="tx-review" control={form.control} name="review" options={REVIEW_STATUSES.map((r) => ({ value: r, label: t(`finance.review.${r}`) }))} />
          </Field>
          <Field id="tx-note" label={t('finance.tx.accountantNote')}>
            <Input id="tx-note" maxLength={240} {...form.register('accountantNote')} />
          </Field>
          <CheckRow {...form.register('hasDocument')} hint={t('finance.tx.form.hasDocumentHint')}>
            {t('finance.tx.hasDocument')}
          </CheckRow>
        </FormSection>
      </div>
    </Modal>
  )
}
